import React, { useMemo, useState } from "react";
import { ClipboardCheck } from "lucide-react";
import {
  AppCard, AppCardContent, AppDataTable, EmptyState, FilterToolbar, StatusBadge, humanise,
} from "../../../ui";
import Shimmer from "../../../Common/Shimmer";
import { DASH, formatDate } from "../../../../utils/format";
import RecordCardHead, { TableHeading } from "../RecordCardHead";
import RecordStat from "../RecordStat";
import { isRecorded, summarizeAssessments } from "../studentRecord";

const PAGE_SIZE = 10;

const ASSESSMENT_COLUMNS = [
  {
    key: "title",
    label: "Assessment",
    render: (q) => <span className="tw-font-medium tw-text-content">{q.title}</span>,
  },
  { key: "type", label: "Type", render: (q) => (q.type ? humanise(q.type) : DASH) },
  {
    key: "score",
    label: "Score",
    align: "right",
    render: (q) => (
      <span className="tw-tabular-nums">
        {isRecorded(q.percentage) ? `${Math.round(q.percentage)}%` : DASH}
        {q.passingScore != null && <span className="tw-text-content-subtle"> · pass {q.passingScore}%</span>}
      </span>
    ),
  },
  {
    key: "result",
    label: "Result",
    render: (q) => <StatusBadge status={q.passed ? "passed" : "failed"} domain="assessmentResult" />,
  },
  {
    key: "submitted",
    label: "Submitted",
    render: (q) => <span className="tw-tabular-nums tw-text-content-muted">{formatDate(q.submittedAt)}</span>,
  },
];

/** One practice area — a titled card of figures. */
const PracticeCard = ({ title, description, loading, children }) => (
  <AppCard>
    <RecordCardHead title={title} description={description} />
    <AppCardContent>
      {loading ? <Shimmer height={56} /> : (
        <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-5">{children}</dl>
      )}
    </AppCardContent>
  </AppCard>
);

/**
 * PerformanceTab — how the student does when they are tested: assessments,
 * coding assignments and practice, AI practice.
 *
 * Mock interviews are on Placement, beside the real interviews they prepare for.
 *
 * Props:
 *   stats     — the `/dashboard/student/:id` resource
 *   portfolio — the `/portfolio/student/:id` resource; `earned.quizzes` is the
 *               student's submitted assessments, newest first
 *   features  — which plan features are on
 */
const PerformanceTab = ({ stats, portfolio, features }) => {
  const [page, setPage] = useState(1);
  const quizzes = useMemo(() => portfolio.data?.earned?.quizzes || [], [portfolio.data]);
  const summary = summarizeAssessments(quizzes);
  const s = stats.data;
  const statsLoading = stats.loading && !s;
  const totalPages = Math.max(1, Math.ceil(quizzes.length / PAGE_SIZE));
  const pageRows = quizzes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const showAssessments = features.quizAssessment || quizzes.length > 0;

  if (!showAssessments && !features.codingPractice && !features.aiLearning) {
    return (
      <EmptyState
        icon={ClipboardCheck}
        title="Nothing to measure yet"
        description="Assessments, coding practice and AI practice are not switched on for this institute, so there are no scores to show."
      />
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {showAssessments && (
        <AppDataTable
          fill={false}
          toolbar={(
            <FilterToolbar flush>
              <TableHeading
                title="Assessments"
                count={summary.taken
                  ? [
                    `${summary.taken} taken`,
                    `${summary.passed} passed`,
                    summary.avgPercent !== null && `${summary.avgPercent}% average`,
                  ].filter(Boolean).join(" · ")
                  : ""}
              />
            </FilterToolbar>
          )}
          columns={ASSESSMENT_COLUMNS}
          rows={pageRows}
          rowKey={(q) => q.id}
          loading={portfolio.loading && !portfolio.data}
          emptyMessage={portfolio.error ? "Could not load assessment results." : "No assessments submitted yet."}
          pagination={{ page, totalPages, total: quizzes.length, pageSize: PAGE_SIZE, onChange: setPage, infoLabel: "assessments" }}
        />
      )}

      <div className="tw-grid tw-items-start tw-gap-4 min-[992px]:tw-grid-cols-2 min-[1200px]:tw-grid-cols-3">
        {features.codingPractice && (
          <PracticeCard title="Coding assignments" description="Assigned to their batch" loading={statsLoading}>
            <RecordStat label="Assigned" value={s?.assignments?.totalAssignments ?? DASH} />
            <RecordStat label="Attempted" value={s?.assignments?.attempted ?? DASH} />
            <RecordStat label="Not started" value={s?.assignments?.pending ?? DASH} />
            <RecordStat
              label="Test cases"
              value={s?.assignments ? `${s.assignments.passedAnswers} passed` : DASH}
              hint={s?.assignments?.failedAnswers ? `${s.assignments.failedAnswers} failed` : undefined}
            />
          </PracticeCard>
        )}

        {features.codingPractice && (
          <PracticeCard title="Coding practice" description="Questions they chose to practise" loading={statsLoading}>
            <RecordStat label="Tried" value={s?.practice?.totalQuestions ?? DASH} hint="questions" />
            <RecordStat label="Solved" value={s?.practice?.solved ?? DASH} />
            <RecordStat label="Partly solved" value={s?.practice?.partial ?? DASH} />
            <RecordStat label="Submissions" value={s?.practice?.totalAttempts ?? DASH} />
          </PracticeCard>
        )}

        {features.aiLearning && (
          <PracticeCard title="AI practice" description="Questions answered with AI feedback" loading={statsLoading}>
            <RecordStat label="Answered" value={s?.ai?.totalQuestions ?? DASH} hint="questions" />
            <RecordStat label="Average score" value={s?.ai?.totalQuestions ? `${s.ai.avgScore}%` : DASH} />
            <RecordStat label="Passed" value={s?.ai?.passed ?? DASH} hint="scored 50% or more" />
            <RecordStat label="Below 50%" value={s?.ai?.failed ?? DASH} />
          </PracticeCard>
        )}
      </div>
    </div>
  );
};

export default PerformanceTab;
